/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business QA責任評価一覧ページ。フィルタ付きテーブルでQA責任評価を表示し、詳細ページへ遷移
 * @graph-connects useQaResponsibilities [calls] QA責任評価一覧フック
 */
import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useQaResponsibilities } from '../../hooks/useQaResponsibility';
import { TEAM_OPTIONS } from '@cortex/domain-product-innovation-lab-kpi-team-filter';

/** @graph-connects none */
export const Route = createFileRoute('/qa-responsibility/')({
  component: QaResponsibilityListPage,
});

/** @graph-connects none */
function formatDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** @graph-connects none */
const PERIOD_OPTIONS = ['30', '90', '180', '365', 'all'] as const;

/** @graph-connects none */
function qaBadgeClass(value: string): string {
  // '有' = QA責任あり（注意喚起）、'無' = なし
  return value === '有'
    ? 'bg-red-100 text-red-700'
    : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]';
}

/** @graph-connects useQaResponsibilities [calls] QA責任評価一覧フック */
function QaResponsibilityListPage() {
  const { t } = useTranslation();
  const [qaReviewStatus, setQaReviewStatus] = useState<string>('unreviewed');
  const [team, setTeam] = useState<string>('');
  const [period, setPeriod] = useState<string>('90');
  const [startDate, setStartDate] = useState<string>(() => {
    const start = new Date();
    start.setDate(start.getDate() - 90);
    return formatDate(start);
  });
  const [endDate, setEndDate] = useState<string>(() => formatDate(new Date()));

  const handlePeriodChange = (value: string) => {
    setPeriod(value);
    if (value === 'all') {
      setStartDate('');
      setEndDate('');
    } else {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - Number(value));
      setStartDate(formatDate(start));
      setEndDate(formatDate(end));
    }
  };

  const { data, isLoading } = useQaResponsibilities({
    qaReviewStatus,
    team: team || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('nav.qaResponsibility', 'QA責任評価')}
        </h1>
        <p className="mt-1 text-[var(--color-text-secondary)]">
          {t('qaResponsibility.listDescription', 'GeminiによるQA責任有無の判定結果の確認・確定')}
        </p>
      </div>

      {/* フィルタ */}
      <Card className="p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
              {t('qaResponsibility.period', '期間')}
            </label>
            <select
              value={period}
              onChange={(e) => {
                handlePeriodChange(e.target.value);
              }}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm"
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt === 'all'
                    ? t('qaResponsibility.filterAll', 'すべて')
                    : `${opt}${t('qaResponsibility.days', '日')}`}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
              {t('qaResponsibility.team', 'チーム')}
            </label>
            <select
              value={team}
              onChange={(e) => {
                setTeam(e.target.value);
              }}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm"
            >
              <option value="">{t('qaResponsibility.filterAll', 'すべて')}</option>
              {TEAM_OPTIONS.filter((o) => o.value !== 'all').map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">
              {t('qaResponsibility.reviewStatus', '確定状態')}
            </label>
            <select
              value={qaReviewStatus}
              onChange={(e) => {
                setQaReviewStatus(e.target.value);
              }}
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm"
            >
              <option value="all">{t('qaResponsibility.filterAll', 'すべて')}</option>
              <option value="unreviewed">{t('qaResponsibility.unreviewed', '仮（未確定）')}</option>
              <option value="reviewed">{t('qaResponsibility.reviewed', '確定済み')}</option>
            </select>
          </div>
          <div className="text-sm text-[var(--color-text-muted)]">
            {data ? `${String(data.total)}${t('qaResponsibility.items', '件')}` : ''}
          </div>
        </div>
      </Card>

      {/* テーブル（デスクトップ） */}
      <Card className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">
                {t('qaResponsibility.issueKey', '課題キー')}
              </th>
              <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">
                {t('qaResponsibility.summary', 'サマリー')}
              </th>
              <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">
                {t('qaResponsibility.team', 'チーム')}
              </th>
              <th className="px-4 py-3 text-left font-medium text-[var(--color-text-secondary)]">
                {t('qaResponsibility.bugType', '種別')}
              </th>
              <th className="px-4 py-3 text-center font-medium text-[var(--color-text-secondary)]">
                {t('qaResponsibility.qaResponsibility', 'QA責任')}
              </th>
              <th className="px-4 py-3 text-center font-medium text-[var(--color-text-secondary)]">
                {t('qaResponsibility.status', '確定')}
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              [1, 2, 3].map((i) => (
                <tr key={i} className="border-b border-[var(--color-border)]">
                  <td className="px-4 py-3" colSpan={6}>
                    <div className="h-4 w-full rounded bg-[var(--color-bg-elevated)] animate-pulse" />
                  </td>
                </tr>
              ))
            ) : data?.data.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-[var(--color-text-muted)]">
                  {t('status.noData', 'データがありません')}
                </td>
              </tr>
            ) : (
              data?.data.map((item) => (
                <tr
                  key={item.issueKey}
                  className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]"
                >
                  <td className="px-4 py-3">
                    <Link
                      to="/qa-responsibility/$issueKey"
                      params={{ issueKey: item.issueKey }}
                      className="text-[var(--color-accent-primary)] hover:underline font-mono text-xs"
                    >
                      {item.issueKey}
                    </Link>
                  </td>
                  <td className="px-4 py-3 max-w-xs truncate">{item.summary}</td>
                  <td className="px-4 py-3 text-xs">
                    {item.devLeaderTeam || item.responsibleTeam}
                  </td>
                  <td className="px-4 py-3 text-xs">{item.bugType}</td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-xs ${qaBadgeClass(item.qaResponsibility)}`}
                    >
                      {item.qaResponsibility || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {item.isQaReviewed ? (
                      <span className="inline-block rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                        {t('qaResponsibility.done', '確定')}
                      </span>
                    ) : (
                      <span className="inline-block rounded-full bg-yellow-100 px-2 py-0.5 text-xs text-yellow-700">
                        {t('qaResponsibility.pending', '仮')}
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      {/* カードビュー（モバイル） */}
      <div className="block md:hidden space-y-3">
        {isLoading
          ? [1, 2, 3].map((i) => (
              <Card key={i} className="p-4">
                <div className="h-4 w-3/4 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
                <div className="h-3 w-1/2 mt-2 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
              </Card>
            ))
          : data?.data.map((item) => (
              <Link
                key={item.issueKey}
                to="/qa-responsibility/$issueKey"
                params={{ issueKey: item.issueKey }}
              >
                <Card className="p-4 hover:bg-[var(--color-bg-hover)]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-xs text-[var(--color-accent-primary)]">
                      {item.issueKey}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${qaBadgeClass(item.qaResponsibility)}`}
                    >
                      {item.qaResponsibility || '-'}
                    </span>
                  </div>
                  <div className="text-sm truncate">{item.summary}</div>
                  <div className="mt-1 text-xs text-[var(--color-text-muted)]">
                    {(item.devLeaderTeam || item.responsibleTeam) && (
                      <span>{item.devLeaderTeam || item.responsibleTeam} ・ </span>
                    )}
                    {item.bugType} ・{' '}
                    {item.isQaReviewed
                      ? t('qaResponsibility.reviewed', '確定済み')
                      : t('qaResponsibility.unreviewed', '仮')}
                  </div>
                </Card>
              </Link>
            ))}
      </div>
    </div>
  );
}
